import React, { useState, useRef, useEffect, useContext } from 'react'
import { StaticQuery, graphql } from 'gatsby'
import { Stamp2020, FacebookIcon, TwitterIcon, InstagramIcon, InfoIcon, InfoCloseIcon } from './icons'
import { ChapterMenu } from './menu'
import { TransitionCover } from '../components/transition'
import { FooterTray } from '../components/footer'
import * as Context from '../components/context'
import { useMediaQuery } from 'react-responsive'
import { useEase } from './motion'
import { motion, useElementScroll } from 'framer-motion';
import { StickyFooterNav } from './stickyFooter'

/*
 * Viewport Component
 *
 */
export const Viewport = (props) => {
	const [ menuOpen, setMenuOpen ] = useState( false );
	const [ footerOpen, setFooterOpen ] = useState( false );
	const [ breakpoint, setBreakpoint ] = useState();
	const toggleMenu = () => setMenuOpen( (currentState) => (!currentState) );
	const toggleFooter = () => setFooterOpen( (currentState) => (!currentState) );
	const toggleBreakpoint = () => setBreakpoint( isDesktop ? 'desktop' : 'mobile' );

	const isDesktop = useMediaQuery({ minWidth: 992 });

	const scrollRef = useRef()
	const { scrollYProgress } = useElementScroll(scrollRef)

	useEffect(() => {
		window.addEventListener('resize', toggleBreakpoint );
		toggleBreakpoint();
	})

	const menuState = menuOpen ? 'menu-open': 'menu-closed'
	const footerState = footerOpen ? 'tray-open': 'tray-closed'
	const viewClasses = `viewport ${menuState} ${footerState}`

	return (
		<Context.Template.Consumer>
			{ context => (
				<div className={`${context.pageID}-template ${viewClasses}`}>

					<div className='stamp-2020'>
						<Stamp2020 />
					</div>

					<motion.nav
						className={ `navBar navBar-${context.navTheme}` }
					>
						<MenuTrigger handleClick={toggleMenu} menuOpen={menuOpen} />
						<Branding />
						{ menuOpen &&
							<UtilityNav footerClick={toggleFooter} menuOpen={menuOpen} />
						}
						{ !menuOpen &&
							<div className='utilityPlaceholder'></div>
						}
					</motion.nav>

					<ChapterMenu menuOpen={menuOpen} menuBreakpoint={breakpoint} />

					{ isDesktop &&
						<FooterTray footerOpen={footerOpen} />
					}

					<div className='inner-viewport'>

						<div className='main container' ref={scrollRef}>
							{props.children}
						</div>

					</div>

					<StickyFooterNav
						index={context.number}
						theme={context.footerTheme}
						scroll={scrollYProgress}
						menuOpen={menuOpen}
						breakpoint={breakpoint}
						currentMomentTheme={context.navTheme}
					/>

				</div>
			)}
		</Context.Template.Consumer>
	);

}

/*
 * MenuTrigger Component
 *
 */
export const MenuTrigger = (props) => {
	const menuVar = {
		closed: {
			opacity: 1,
			transition: {
				staggerChildren: 0,
				duration: 1.5,
			}
		},
		open: {
			opacity: 1,
			transition: {
				duration: 1,
			}
		},
		menuHover: {
			opacity: 1,
			transition: {
				duration: 0.5,

			}
		}
	}

	const topVar = {
		closed: {
			opacity: 1,
			translateX: 0,
		},
		open: {
			opacity: 0,
			translateX: -50,
		},
		menuHover: {
			translateX: -5,
		}
	}

	const middleVar = {
		closed: {
			opacity: 1,
			translateX: 0,
		},
		open: {
			opacity: 0,
			translateX: 50,
		},
		menuHover: {
			translateX: 5,
		}
	}

	const bottomLeftVar = {
		closed: {
			opacity: 1,
			x1: 0,
			x2: 12,
		},
		open: {
			opacity: 0,
			x1: 0,
			x2: 0,
		},
		menuHover: {
			x1: 12,
			x2: 15,
		}
	}

	const bottomRightVar = {
		closed: {
			opacity: 1,
			x1: 14,
			x2: 16,
		},
		open: {
			opacity: 0,
			x1: 30,
			x2: 30,
		},
		menuHover: {
			x1: 15,
			x2: 17,
		}

	}

	const textVar = {

	}

	const textLeftVar = {
		closed: {
			marginLeft: 0,
			marginRight: 0,
			opacity: 1,
			x: 0,
		},
		open: custom => ({
			opacity: 0,
			x: -50 * custom,
		}),
		menuHover: {
			marginLeft: 1,
			marginRight: 1,
		}
	}

	const textRightVar = {
		closed: {
			marginLeft: 0,
			marginRight: 0,
			opacity: 1,
			x: 0,
		},
		open: custom=> ({
			opacity: 0,
			x: 50 * custom,
		}),
		menuHover: {
			marginLeft: 1,
			marginRight: 1,
		},
	}

	const varCloseLeft = {
		closed: {
			x: -30,
			y: -30,
			opacity: 0,
			transition: {
				duration: 0.5,
				ease: useEase('easeOutExpo'),
			},
		},
		open: {
			x: 0,
			y: 0,
			opacity: 1,
			transition: {
				duration: 0.2,
				ease: useEase('easeOutExpo'),
				delay: 0.5,
			},
		}
	}
	const varCloseRightTop = {
		closed: {
			x: 30,
			y: -30,
			opacity: 0,
			transition: {
				duration: 0.5,
				ease: useEase('easeOutExpo'),
			},
		},
		open: {
			x: 0,
			y: 0,
			opacity: 1,
			transition: {
				duration: 0.2,
				ease: useEase('easeOutExpo'),
				delay: 0.4,
			},
		},
		closeHover: {
			x: 3,
			y: -3,
		}
	}

	const varCloseRightBottom = {
		closed: {
			x: -30,
			y: 30,
			opacity: 0,
			transition: {
				duration: 0.5,
				ease: useEase("easeOutExpo"),
			},
		},
		open: {
			x: 0,
			y: 0,
			opacity: 1,
			transition: {
				duration: 0.2,
				ease: useEase("easeOutExpo"),
				delay: 0.7,
			},
		},
		closeHover: {
			x: -3,
			y: 3,
		}
	}

	const classes = `un-button menu-trigger`

	return (
		<motion.button
			className={classes}
			onClick={props.handleClick}
			variants={menuVar}
			whileHover={props.menuOpen ? 'closeHover' : 'menuHover'}
			initial="closed"
			animate={props.menuOpen ? "open" : "closed"}
		>
			<motion.svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 30 18"
				id="menu-icon"
			>
				<g id="menuBurger">
					<motion.line
						id="top_bun"
						y1="1"
						x2="30"
						y2="1"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						variants={topVar}
					/>
					<motion.line
						id="cheese_slice"
						y1="9"
						x2="30"
						y2="9"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						variants={middleVar}
					/>
					<motion.line
						id="bottom_bun_left"
						y1="17"
						x2="12"
						y2="17"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						variants={bottomLeftVar}
					/>
					<motion.line
						id="bottom_bun_right"
						x1="14"
						y1="17"
						x2="16"
						y2="17"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						variants={bottomRightVar}
					/>
				</g>
			</motion.svg>
			<motion.div className="menu-trigger-text" variants={textVar} layout>
				<motion.span custom={2} variants={textLeftVar}>
					M
				</motion.span>
				<motion.span custom={1} variants={textLeftVar}>
					E
				</motion.span>
				<motion.span custom={1} variants={textRightVar}>
					N
				</motion.span>
				<motion.span custom={2} variants={textRightVar}>
					U
				</motion.span>
			</motion.div>

			<motion.svg
				id="menu-close"
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 28.41 28.41"
			>
				<g id="menu-close-container">
					<motion.line
						id="left"
						variants={varCloseLeft}
						x1="0.71"
						y1="0.71"
						x2="27.71"
						y2="27.71"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					/>
					<motion.line
						id="right_top"
						variants={varCloseRightTop}
						x1="18.71"
						y1="9.71"
						x2="27.71"
						y2="0.71"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					/>
					<motion.line
						id="right_bottom"
						variants={varCloseRightBottom}
						x1="0.71"
						y1="27.71"
						x2="9.71"
						y2="18.71"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					/>
				</g>
			</motion.svg>
		</motion.button>
	)

}

/*
 * Branding Component
 *
 */
export const Branding = (props) => {
	return (
		<div className='bu-branding'>
			<TransitionCover
				cover
				to={`/`}
				direction='up'
				duration={1.8}
				bg="#ff0000"
			>
				<span>Boston University</span> Year in Review
			</TransitionCover>
		</div>
	)
}

/*
 * UtilityNav Component
 *
 */
export const UtilityNav = (props) => {

	const navAnime = {
		closed: {
			opacity: 0,
			transition: {
				ease: useEase('easeOutExpo'),
				duration: 0.5,
				delay: 0.4,
			},
		},
		open: {
			opacity: 1,
			transition: {
				ease: useEase('easeInExpo'),
				duration: .6,
			}
		}
	}

	return (
		<StaticQuery
			query = { graphql`
				query {
					strapiUtilityNav {
						facebook_url
						twitter_url
						instagram_url
					}
				}
			`}
			render={ data => (
				<motion.nav
					className='utility-nav'
					variants={navAnime}
					initial='closed'
					animate={props.menuOpen? 'open' : 'closed' }
					exit='closed'
				>
					<a href={data.strapiUtilityNav.facebook_url} className='utility-link facebook-icon' target="_blank">
						<FacebookIcon />
					</a>
					<a href={data.strapiUtilityNav.twitter_url} className='utility-link twitter-icon' target="_blank">
						<TwitterIcon />
					</a>
					<a href={data.strapiUtilityNav.instagram_url} className='utility-link instagram-icon' target="_blank">
						<InstagramIcon />
					</a>
					<button className='utility-link un-button tray-trigger' onClick={props.footerClick}>
						<InfoIcon />
						<InfoCloseIcon />
					</button>
				</motion.nav>
			)}
		/>
	)

}
